
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getFormalName } from '@/lib/teams';
// AuthProvider removed - using mock user for development

interface UserSelection {
  id: string;
  team_name: string;
  matchday: number;
  season: string;
  used_double_down: boolean;
  points_earned: number;
  match_result: 'win' | 'draw' | 'loss' | null;
  processed: boolean;
  created_at: string;
  autopicked_at: string | null;
  is_forfeit: boolean | null;
}

interface UserSelectionData {
  selections: UserSelection[];
  teamUsage: { [key: string]: number };
  tddUsage: { [key: string]: number };
  totalTddUsed: number;
  tddRemaining: number;
}

export const useUserSelections = (userId?: string, leagueId?: string, season?: string) => {
  const [selectionData, setSelectionData] = useState<UserSelectionData>({
    selections: [],
    teamUsage: {},
    tddUsage: {},
    totalTddUsed: 0,
    tddRemaining: 4
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId && leagueId) {
      fetchUserSelections(userId);
    } else if (!userId) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, leagueId, season]);

const fetchUserSelections = async (id?: string) => {
  try {
    setIsLoading(true);
    const targetId = id ?? userId;
    if (!targetId) {
      setError('User not selected');
      return;
    }

    const { data, error } = await supabase.functions.invoke('get-user-selections', {
      body: { userId: targetId, leagueId, ...(season ? { season } : {}) }
    });

      if (error) {
        console.error('Error fetching user selections:', error);
        setError('Failed to fetch selection history');
        return;
      }

      if (data) {
        setSelectionData(data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching user selections:', err);
      setError('Failed to fetch selection history');
    } finally {
      setIsLoading(false);
    }
  };

  // Backend returns teamUsage/tddUsage keyed by normalized lowercase names,
  // so we normalize lookups the same way.
  const normalizeKey = useCallback((teamName: string) => {
    return getFormalName(teamName).toLowerCase();
  }, []);

  const getTeamUsageCount = useCallback((teamName: string) => {
    return selectionData.teamUsage[normalizeKey(teamName)] || 0;
  }, [selectionData.teamUsage, normalizeKey]);

  const canSelectTeam = useCallback((teamName: string) => {
    return (selectionData.teamUsage[normalizeKey(teamName)] || 0) < 2;
  }, [selectionData.teamUsage, normalizeKey]);

  const canUseTddForTeam = useCallback((teamName: string) => {
    const hasUsedTddForTeam = (selectionData.tddUsage[normalizeKey(teamName)] || 0) > 0;
    return selectionData.tddRemaining > 0 && !hasUsedTddForTeam;
  }, [selectionData.tddUsage, selectionData.tddRemaining, normalizeKey]);

  return {
    selectionData,
    isLoading,
    error,
    getTeamUsageCount,
    canSelectTeam,
    canUseTddForTeam,
    refetch: () => fetchUserSelections()
  };
};
